import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage.jsx";
import { API_BASE_URL } from "../config.tsx";
import { useTranslation } from "react-i18next";
import apiFetch from "../components/helpers/requests";

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthContextType {
  token: string | null;
  user: string | null;
  userId: string;
  userRole: string | null;
  headquarterId: string | null;
  headsetId: string | null; // Added this line
  firstName: string | null;
  lastName: string | null;
  language: string | null;
  login: (email: string, password: string) => void;
  register: (
    email: string,
    firstName: string,
    lastName: string,
    headsetId: string,
    headquarterId: string,
    hospitalId: string,
    role: string
  ) => any;
  setSession: (userData: any) => void;
  registerUsers: (users: any[]) => any;
  repeatVerification: () => void;
  logout: () => void;
  changeLanguage: (newLanguage: string) => Promise<void>;
  isLanguageReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const signIn = async (email, password) => {
  return apiFetch("/signin", {
    method: "POST",
    body: JSON.stringify({ email, password })
  }, true);
};

const signUp = async (
  email,
  firstName,
  lastName,
  headsetId,
  headquarterId,
  hospitalId,
  role
) => {
  const userData = await apiFetch("/signup", {
    method: "POST",
    body: JSON.stringify({
      email,
      firstName,
      lastName,
      headsetId,
      headquarterId,
      hospitalId,
      role,
    })
  }, true);

  if (userData.error) {
    alert(userData.error);
    return null;
  } else {
    return userData;
  }
};

const createMultipleUsers = async (users) => {
  const result = await apiFetch("/createMultipleUsers", {
    method: "POST",
    body: JSON.stringify(users)
  }, false); // Not public, requires auth (usually) - wait, signup is usually public, but bulk creation is admin only.
             // If I set isPublic=false, it uses the token from localStorage.
             // Admin creating users SHOULD be authenticated.

  if (result.error) {
    alert(result.error);
    return null;
  } else {
    return result;
  }
};

const resendEmailVerification = async (email) => {
  return apiFetch("/resendEmailVerification", {
    method: "POST",
    body: JSON.stringify({ email })
  }, true);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [token, setToken] = useLocalStorage("token", null);
  const [userId, setUserId] = useLocalStorage("userId", null);
  const [userRole, setRole] = useLocalStorage("userRole", null);
  const [headquarterId, setHeadquarterId] = useLocalStorage(
    "headquarterId",
    null
  );
  const [headsetId, setHeadsetId] = useLocalStorage("headsetId", null);
  const [firstName, setFirstName] = useLocalStorage("firstName", null);
  const [lastName, setLastName] = useLocalStorage("lastName", null);
  const [language, setLanguage] = useLocalStorage("language", "en");
  const [isLanguageReady, setIsLanguageReady] = useState(false);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeLanguage = async () => {
      if (i18n.language !== language) {
        await i18n.changeLanguage(language);
      }
      setIsLanguageReady(true);
    };
    initializeLanguage();
  }, [i18n, language]);

  const login = async (email: string, password: string) => {
    const userData = await signIn(email, password);
    setSession(userData);
  };

  const setSession = (userData: any) => {
    setUser(userData.email || userData.userId); // Fallback if email not directly in root
    setUserId(userData.userId);
    setToken(userData.token);

    if (!userData.verification) {
      navigate("/verification");
      return;
    }

    setRole(userData.role);
    setHeadquarterId(userData.headquarterId);

    // Support backend returning either headsetId (new) or groupId (old compatibility)
    const resolvedHeadsetId = userData.headsetId || userData.groupId;
    setHeadsetId(resolvedHeadsetId);

    setFirstName(userData.firstName);
    setLastName(userData.lastName);

    if (userData.role === "admin") {
      navigate("/headquarterList");
    } else {
      navigate("/main");
    }
  };

  const register = async (
    email: string,
    firstName: string,
    lastName: string,
    headsetId: string,
    headquarterId: string,
    hospitalId: string,
    role: string
  ) => {
    const userData = await signUp(
      email,
      firstName,
      lastName,
      headsetId,
      headquarterId,
      hospitalId,
      role
    );

    if (userData) {
      setUser(email);
    }

    return userData;
  };

  const registerUsers = async (users: any[]) => {
    return await createMultipleUsers(users);
  };

  const repeatVerification = async () => {
    const result = await resendEmailVerification(user);
    if (!result) {
      navigate("/main");
      return;
    }
  };

  const logout = () => {
    setUser(null);
    setUserId(null);
    setRole(null);
    setHeadquarterId(null);
    setHeadsetId(null);
    setFirstName(null);
    setLastName(null);
    setToken(null);
    navigate("/login");
  };

  const changeLanguage = async (newLanguage: string) => {
    await i18n.changeLanguage(newLanguage);
    setLanguage(newLanguage);
  };

  const value = useMemo(
    () => ({
      token,
      user,
      userId,
      userRole,
      headquarterId,
      headsetId,
      firstName,
      lastName,
      language,
      login,
      register,
      registerUsers,
      repeatVerification,
      logout,
      changeLanguage,
      isLanguageReady,
      setSession,
    }),
    [
      token,
      user,
      userId,
      userRole,
      headquarterId,
      headsetId,
      firstName,
      lastName,
      language,
      isLanguageReady,
      setSession,
    ]
  );

  if (!isLanguageReady) {
    return <></>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
